import { useState, useEffect } from 'react';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../ui/card';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Separator } from '../ui/separator';
import { Shield, BellOff, Users } from 'lucide-react';
import { toast } from 'sonner';

export const DataPrivacyCard = ({ user }) => {
  const [dataConsent, setDataConsent] = useState(true);
  const [anonymousConsent, setAnonymousConsent] = useState(false);
  const [emailUnsubscribed, setEmailUnsubscribed] = useState(false);
  const [compareStatsOptedIn, setCompareStatsOptedIn] = useState(false);

  useEffect(() => {
    if (user) {
      setDataConsent(user.data_consent !== false);
      setAnonymousConsent(user.anonymous_data_consent === true);
      setEmailUnsubscribed(user.email_unsubscribed === true);
      setCompareStatsOptedIn(user.compare_stats_opted_in === true);
    }
  }, [user]);

  const handleConsentChange = async (key, value) => {
    try {
      await api.put('/user/consent', { [key]: value });
      if (key === 'data_consent') setDataConsent(value);
      if (key === 'anonymous_data_consent') setAnonymousConsent(value);
      if (key === 'compare_stats_opted_in') setCompareStatsOptedIn(value);
      toast.success('Settings updated');
    } catch { toast.error('Failed to update settings'); }
  };

  const handleEmailPreferenceChange = async (unsubscribed) => {
    try {
      await api.put('/user/email-preferences', { email_unsubscribed: unsubscribed });
      setEmailUnsubscribed(unsubscribed);
      toast.success(unsubscribed ? 'You have been unsubscribed from all emails' : 'Email notifications re-enabled');
    } catch { toast.error('Failed to update email preferences'); }
  };

  return (
    <Card className="glass border-white/20 dark:border-white/10">
      <CardHeader>
        <CardTitle className="font-['Manrope'] flex items-center gap-2">
          <Shield className="w-5 h-5" /> Data & Privacy
        </CardTitle>
        <CardDescription>Manage your data in accordance with UK/EU GDPR</CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Store my data</Label>
            <p className="text-sm text-muted-foreground">Save interests and achievements to your account</p>
          </div>
          <Switch checked={dataConsent} onCheckedChange={(v) => handleConsentChange('data_consent', v)} data-testid="data-consent-switch" />
        </div>
        <Separator />
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label>Anonymous analytics</Label>
            <p className="text-sm text-muted-foreground">Help improve Brioritizer with anonymous usage data</p>
          </div>
          <Switch checked={anonymousConsent} onCheckedChange={(v) => handleConsentChange('anonymous_data_consent', v)} data-testid="anonymous-consent-switch" />
        </div>
        <Separator />
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="flex items-center gap-2"><BellOff className="w-4 h-4" /> Unsubscribe from all emails</Label>
            <p className="text-sm text-muted-foreground">Stop receiving all emails from Brioritizer, including weekly check-ins</p>
          </div>
          <Switch checked={emailUnsubscribed} onCheckedChange={handleEmailPreferenceChange} data-testid="email-unsubscribe-switch" />
        </div>
        <Separator />
        <div className="flex items-center justify-between">
          <div className="space-y-0.5">
            <Label className="flex items-center gap-2"><Users className="w-4 h-4" /> Compare my stats to other users</Label>
            <p className="text-sm text-muted-foreground">Anonymously compare your achievements and wellbeing with other Brioritizer users</p>
          </div>
          <Switch checked={compareStatsOptedIn} onCheckedChange={(v) => handleConsentChange('compare_stats_opted_in', v)} data-testid="compare-stats-switch" />
        </div>
      </CardContent>
    </Card>
  );
};
